// Single source of truth for the CSS font stack used by every Chromium-printed page: the A5 digital + print
// books (lib/pdf/html-book-templates.js) and the hardcover text pages (services/print/hardcover/html.ts).
// All families are installed in the worker images (fonts-noto*). Chromium picks, per character, the first family
// in the list that covers it, so one stack serves every language; without the script families Chromium falls back
// to FreeMono/FreeSans/FreeSerif/WenQuanYi (verified for ar/he/ta/ja).
// CommonJS so both the plain-JS PDF code and TypeScript can use it.
//
// Book typography (both engines: the Chromium templates + hardcover, and PDFKit): bodyFontFor/titleFont name the
// bundled Literata / Andika / Fraunces files in public/fonts/book by reading band. Ruling R4: Noto stays the
// fallback, so a text block whose characters the band font does not all cover keeps today's font, size and
// line height (resolveBodyText reports covered:false and the renderer keeps its old path).
const fs = require('fs');
const path = require('path');

// [serif face, sans face] per script, in a fixed order.
const SCRIPT_FAMILIES = [
  ['Noto Naskh Arabic', 'Noto Sans Arabic'],
  ['Noto Serif Hebrew', 'Noto Sans Hebrew'],
  ['Noto Serif Devanagari', 'Noto Sans Devanagari'],
  ['Noto Serif Tamil', 'Noto Sans Tamil'],
  ['Noto Serif Telugu', 'Noto Sans Telugu'],
  ['Noto Serif Malayalam', 'Noto Sans Malayalam'],
  ['Noto Serif Kannada', 'Noto Sans Kannada'],
  ['Noto Serif Gurmukhi', 'Noto Sans Gurmukhi'],
  ['Noto Serif Bengali', 'Noto Sans Bengali'],
  ['Noto Serif Gujarati', 'Noto Sans Gujarati'],
  ['Noto Serif CJK JP', 'Noto Sans CJK JP'],
  ['Noto Serif CJK SC', 'Noto Sans CJK SC'],
  ['Noto Serif CJK KR', 'Noto Sans CJK KR'],
];

/**
 * @param {'serif'|'sans'} style Which face leads, for Latin and for each script.
 * @returns {string} A font-family value, ending in a generic family.
 */
function fontStack(style = 'serif') {
  const serifFirst = style !== 'sans';
  const latin = serifFirst ? ['Noto Serif', 'Noto Sans'] : ['Noto Sans'];
  const scripts = SCRIPT_FAMILIES.flatMap(([serif, sans]) => (serifFirst ? [serif, sans] : [sans, serif]));
  return [...latin, ...scripts].map((f) => `'${f}'`).join(', ') + (serifFirst ? ', serif' : ', sans-serif');
}

// Italic is a Latin-script convention; a synthetic slant on Arabic/Indic/CJK looks wrong.
const isLatinOnly = (t) => /^[\s\p{P}\p{N}\p{S}\u0000-ɏḀ-ỿ]*$/u.test(String(t ?? ''));

const A5_TRIM_WIDTH_PT = 419.53;
const BOOK_TEXT_COLOR = '#222222';

function findFontDir() {
  const candidates = [
    path.join(process.cwd(), 'public', 'fonts', 'book'),
    path.join(__dirname, '..', '..', 'public', 'fonts', 'book'),
  ];
  // Client bundles reach this module (lib/books/product-type -> facing-text-layout) with `fs` stubbed as {}:
  // module load must not throw there, or every page importing product-type crashes.
  if (typeof fs.existsSync !== 'function') return candidates[0];
  return candidates.find((d) => fs.existsSync(d)) || candidates[0];
}
const BOOK_FONT_DIR = findFontDir();
const fontPath = (name) => path.join(BOOK_FONT_DIR, name);

// R2. Bold is Literata SemiBold 600; band M bold is Andika Bold 700.
const literata = (weight, file, sizePt, lineHeight, glue) => ({
  family: 'Literata', weight, boldWeight: 600, file: fontPath(file), boldFile: fontPath('Literata-SemiBold.ttf'), sizePt, lineHeight, glue,
});
const BAND_FONTS = {
  T: () => literata(500, 'Literata-Medium.ttf', 22, 1.35, true),
  E: () => literata(500, 'Literata-Medium.ttf', 20, 1.4, true),
  M: () => ({
    family: 'Andika', weight: 400, boldWeight: 700, file: fontPath('Andika-Regular.ttf'), boldFile: fontPath('Andika-Bold.ttf'),
    sizePt: 18, lineHeight: 1.45, glue: true,
  }),
  O: () => literata(400, 'Literata-Regular.ttf', 14, 1.45, false),
  W: () => literata(400, 'Literata-Regular.ttf', 13, 1.45, false),
  G: () => literata(400, 'Literata-Regular.ttf', 11.5, 1.4, false),
};

/** @returns {{family:string,weight:number,boldWeight:number,file:string,boldFile:string,sizePt:number|null,lineHeight:number|null,glue:boolean}} */
function bodyFontFor(band) {
  const make = typeof band === 'string' && Object.prototype.hasOwnProperty.call(BAND_FONTS, band) ? BAND_FONTS[band] : null;
  return make ? make() : literata(400, 'Literata-Regular.ttf', null, null, false);
}

function titleFont() {
  return { family: 'Fraunces', weight: 600, file: fontPath('Fraunces-SemiBold.ttf'), coverStartPt: 48 };
}

const fontCache = new Map(); // file -> fontkit font | null
const warned = new Set();
function openFont(file) {
  if (fontCache.has(file)) return fontCache.get(file);
  let font = null;
  try {
    font = require('fontkit').openSync(file);
  } catch (e) {
    if (!warned.has(file)) {
      warned.add(file);
      console.error(`[font-stack] cannot open book font ${file}: ${String((e && e.message) || e).split('\n')[0]}`);
    }
  }
  fontCache.set(file, font);
  return font;
}

/**
 * Exotic spaces/hyphens the book fonts lack become ones they have: U+202F (French narrow no-break space), U+2000-200A,
 * U+205F, U+3000 -> U+00A0; U+FEFF, U+2060, U+200B removed; U+2011 -> U+2010. Every renderer draws this output.
 */
function normalizeBookText(text) {
  return String(text ?? '')
    .replace(/[\u202F\u2000-\u200A\u205F\u3000]/g, '\u00A0')
    .replace(/[\uFEFF\u2060\u200B]/g, '')
    .replace(/\u2011/g, '\u2010');
}

/** True when the font has a glyph for every code point of text except tab/LF/CR/space/NBSP and C0/C1 controls. */
function coversText(font, text) {
  const f = openFont(font.file);
  if (!f) return false;
  for (const ch of String(text ?? '')) {
    if (/[\u0000-\u001f \u007f-\u00A0]/.test(ch)) continue;
    if (!f.hasGlyphForCodePoint(ch.codePointAt(0))) return false;
  }
  return true;
}

function scaledPt(sizePt, trimWidthPt) {
  return Math.round(((sizePt * trimWidthPt) / A5_TRIM_WIDTH_PT) * 10) / 10;
}

/** R5: per paragraph, NBSP joins the last two words and each 1-2 letter word to the word after it. */
function glueWords(text) {
  return String(text ?? '')
    .split('\n')
    .map((para) => {
      const words = para.split(/ +/).filter((w) => w !== '');
      if (words.length < 3) return para;
      let out = words[0];
      for (let i = 1; i < words.length; i++) {
        const letters = (words[i - 1].match(/\p{L}/gu) || []).length;
        const glue = i === words.length - 1 || (letters >= 1 && letters <= 2);
        out += (glue ? ' ' : ' ') + words[i];
      }
      return out;
    })
    .join('\n');
}

function cssFamily(font) {
  if (font.family === 'Andika') return `'Andika', ${fontStack('sans')}`;
  return `'${font.family}', ${fontStack('serif')}`;
}

const faceCache = new Map(); // file|weight|family -> css
function fontFaceCss(fonts) {
  const seen = new Set();
  const out = [];
  for (const { family, file, weight } of fonts) {
    if (seen.has(file)) continue;
    seen.add(file);
    const key = `${file}|${weight}|${family}`;
    let css = faceCache.get(key);
    if (css === undefined) {
      try {
        const b64 = fs.readFileSync(file).toString('base64');
        css = `@font-face { font-family: '${family}'; font-style: normal; font-weight: ${weight}; font-display: block; src: url(data:font/ttf;base64,${b64}) format('truetype'); }`;
        faceCache.set(key, css);
      } catch (e) {
        console.error(`[font-stack] cannot embed book font ${file}: ${String((e && e.message) || e).split('\n')[0]}`);
        css = '';
      }
    }
    if (css) out.push(css);
  }
  return out.join('\n');
}

function resolveBodyText({ band, text }) {
  const font = bodyFontFor(band);
  const norm = normalizeBookText(text);
  const covered = coversText(font, norm);
  return { covered, font, text: covered && font.glue ? glueWords(norm) : norm };
}

module.exports = {
  SCRIPT_FAMILIES, fontStack, isLatinOnly,
  A5_TRIM_WIDTH_PT, BOOK_TEXT_COLOR, BOOK_FONT_DIR,
  bodyFontFor, titleFont, coversText, normalizeBookText, scaledPt, glueWords, cssFamily, fontFaceCss, resolveBodyText,
};
